<template>
  <div v-if="no_members" class="w-full h-full flex flex-col p-4 bg-white shadow-md rounded-lg">
    <span class="font-bold text-[20px]">{{ $t("Team chat") }}</span>
    <div class="w-full h-full flex items-center justify-center">
      <div class="flex flex-col items-center">
        <EmptyChatIcon class="pointer-events-none" />
        <span class="mt-4">{{ $t("You have no messages yet.") }}</span>
        <span>{{ $t("Start by creating one now!") }}</span>
        <div
          class="mt-8 flex cursor-pointer items-center border-transparent bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] px-6 font-bold text-white rounded-full w-fit-content h-[50px] content-center text-[14px]"
          @click="no_members = false">
          <ShareTeamMembersIcon class="w-7 h-7 ml-1 mr-3" />
          <span> {{ $t("Invite Team Members") }} </span>
        </div>
        <span class="pl-1 bg-gradient-to-r from-[#2da8d7] to-[#467ddf] bg-clip-text text-transparent mt-6">{{
          $t("or send messages directly to")
        }}</span>
        <span class="pl-1 bg-gradient-to-r from-[#2da8d7] to-[#467ddf] bg-clip-text text-transparent">{{
          $t("your Slack workspace")
        }}</span>
      </div>
    </div>
  </div>
  <div v-else class="max-w-lg mx-auto p-4 bg-white shadow-md rounded-lg">
    <!-- Chat Header with User Profiles -->
    <div class="chat-header flex items-center justify-between mb-4">
      <span class="font-bold text-[20px]">{{ $t("Team chat") }}</span>
      <div class="mr-3 flex items-center gap-x-2">
        <div class="flex items-center -space-x-5 overflow-hidden">
          <span v-for="user in users.slice(0, 2)" :key="user.id" class="flex-shrink-0 inline-block has-tooltip">
            <img v-if="user.avatar" class="inline-block rounded-full h-9 w-9 object-cover" :src="user.avatar" alt="">
            <Avatar v-else :first-name="user.first_name" :last-name="user.last_name" class="rounded-full border border-white" />
            <span class="p-1 bg-gray-300 rounded-sm tooltip whitespace-nowrap">
              <div class="">
                <p class="text-sm font-medium dark:!text-white text-gray-700 group-hover:text-gray-900">
                  {{ user.full_name }}
                </p>
              </div>
            </span>
          </span>
          <span
            v-if="users?.length > 2"
            class="flex items-center justify-center font-bold w-9 h-9 border border-white rounded-full bg-light-brand-blue text-sm text-white">
            +{{ users?.length - 2 }}</span>
        </div>
        <div
          class="flex items-center hover:cursor-pointer border-transparent bg-gradient-to-r from-[#2DA7D7] to-[#3B91DB] px-2 font-bold rounded-lg w-fit-content h-[36px] content-center text-[14px]">
          <UserRoundPlus class="mr-2 border-none w-6 h-6 text-white dark:text-gray-500" />
          <span class="text-white whitespace-nowrap">Invite</span>
        </div>
      </div>
    </div>
    <div class="border-t" />
    <!-- Chat Messages -->
    <div class="chat-messages max-h-[500px] min-h-[500px] overflow-y-auto mb-4">
      <template v-for="(group, groupIndex) in groupedMessages" :key="groupIndex">
        <!-- Date Separator -->
        <div class="text-center text-gray-500 my-4">
          {{ $t(formatDate(group.date)) }}
        </div>

        <!-- Messages for the given date -->
        <div
          v-for="(message, message_index) in group.messages"
          :key="message_index"
          :class="['mb-3', message.sender === $store?.state?.auth?.user?.id ? 'ml-12' : '']">
          <div class="flex items-center">
            <!-- Profile photo for other senders -->
            <img :src="getUserPhoto(message.sender)" class="w-8 h-8 rounded-full inline-block flex-shrink-0 mr-2 mb-4" alt="Sender Photo">

            <!-- Message content with different background colors -->
            <div class="flex-1">
              <div
                :class="['p-2 rounded-lg', message.sender === $store?.state?.auth?.user?.id ? 'bg-[#2DA7D7] text-white' : 'bg-[#F4FBFD]']">
                {{ message.text }}
              </div>
              <!-- Display time under the message -->
              <div class="text-xs text-gray-500 text-right mt-0.5">
                {{ formatTime(message.timestamp) }}
              </div>
            </div>

            <!-- Reaction and Reply buttons -->
            <div class="relative flex items-center ml-2">
              <!-- Reaction button opens the horizontal popover -->
              <button class="text-gray-600 hover:text-gray-900" @click="toggleReactionsMenu(message_index)">
                {{ message.reaction ? message.reaction : "☺" }}
              </button>
              <!-- Horizontal Reaction Popover -->
              <div
                v-if="reactions_menu_open === message_index"
                class="absolute bg-white shadow-lg rounded-lg p-2 flex flex-row space-x-2 z-10"
                style="top: 20px; right: 0">
                <button @click="setReaction(message_index, '❤️')">❤️</button>
                <button @click="setReaction(message_index, '😂')">😂</button>
                <button @click="setReaction(message_index, '👍')">👍</button>
                <button @click="setReaction(message_index, '😮')">😮</button>
                <button @click="setReaction(message_index, '😢')">😢</button>
              </div>

              <!-- Reply button -->
              <Undo2 class="w-3 h-3 ml-2 hover:cursor-pointer" @click="setReplyTo(message_index)" />
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Input area for sending messages and attaching files -->
    <div class="flex items-center">
      <input
        v-model="newMessage"
        type="text"
        class="border border-common-grey p-2 rounded-lg flex-grow"
        :placeholder="$t('Type your message...')"
        @keydown.enter="sendMessage">
      <div class="ml-2 flex items-center justify-center border rounded-lg h-10 w-10 bg-white text-black hover:cursor-pointer">
        <input type="file" class="h-10 w-10 custom-file-input" @change="sendFile">
        <Paperclip class="w-6 h-6" />
      </div>
      <div
        class="ml-2 h-10 flex items-center hover:cursor-pointer border-transparent bg-gradient-to-r from-[#2DA7D7] to-[#3B91DB] px-2 font-bold rounded-lg w-fit-content content-center text-[14px]"
        @click="sendMessage">
        <SendHorizontal class="mr-2 border-none w-6 h-6 text-white" />
        <span class="text-white whitespace-nowrap">{{ $t("Send message") }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment"
import { UserRoundPlus, Undo2, SendHorizontal, Paperclip } from "lucide-vue-next"
import Avatar from "@/components/shared/Avatar.vue"
import EmptyChatIcon from "@/components/shared/icons/EmptyChatIcon.vue"
import ShareTeamMembersIcon from "@/components/shared/icons/ShareTeamMembersIcon.vue"

export default {
  components: {
    EmptyChatIcon,
    ShareTeamMembersIcon,
    Undo2,
    SendHorizontal,
    Paperclip,
    Avatar,
    UserRoundPlus
  },
  data() {
    return {
      no_members: true,
      users: [this.$store?.state?.auth?.user],
      /*users: [
        { id: "1", name: this.$store?.state?.auth?.user.first_name, avatar: null },
        { id: "2", name: "Bob", avatar: "/src/assets/person2-img.jpg" },
        { id: "3", name: "Djelloul", avatar: "/src/assets/person3-img.jpg" },
        { id: "4", name: "Bob 2", avatar: "/src/assets/person2-img.jpg" },
        { id: "5", name: "Bob 3", avatar: "/src/assets/person2-img.jpg" }
      ],*/
      messages: [
        /*{
          text: "Hi team, would like us to discuss this opportunity. Mark, can you please share any files that you have on hand?",
          sender: this.$store?.state?.auth?.user.first_name,
          timestamp: new Date(),
          liked: false,
          reaction: "👍"
        },
        {
          text: "Sure thing Jose.",
          sender: "Bob",
          timestamp: new Date(),
          liked: false,
          reaction: ""
        },
        {
          text: "Thanks Mark, great notes there. Let’s schedule a call to discuss more.",
          sender: "Djelloul",
          timestamp: new Date(),
          liked: false,
          reaction: "❤️"
        }*/
      ],
      newMessage: "",
      reply_to: null,
      reactions_menu_open: null // Tracks which message has the reaction menu open
    }
  },
  computed: {
    groupedMessages() {
      const groups = []
      this.messages.forEach((message) => {
        const messageDate = moment(message.timestamp).format("YYYY-MM-DD")
        let lastGroup = groups[groups.length - 1]

        if (!lastGroup || lastGroup.date !== messageDate) {
          lastGroup = { date: messageDate, messages: [] }
          groups.push(lastGroup)
        }

        lastGroup.messages.push(message)
      })

      return groups
    }
  },
  methods: {
    sendMessage() {
      if (this.newMessage.trim()) {
        const newMessage = {
          text: this.newMessage,
          sender: this.$store?.state?.auth?.user?.id,
          timestamp: new Date(),
          liked: false,
          reaction: ""
        }
        this.messages.push(newMessage)
        this.newMessage = ""
        this.reply_to = null
      }
    },
    toggleReactionsMenu(index) {
      if (this.reactions_menu_open === index) {
        this.reactions_menu_open = null // Close the menu if it's already open
      } else {
        this.reactions_menu_open = index // Open the menu for the selected message
      }
    },
    setReaction(index, reaction) {
      this.messages[index].reaction = reaction
      this.reactions_menu_open = null // Close the popover after reaction is set
    },
    setReplyTo(index) {
      this.reply_to = this.messages[index]
    },
    sendFile(event) {
      const file = event.target.files[0]
      if (file) {
        const newMessage = {
          text: `File: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`,
          sender: this.$store?.state?.auth?.user?.id,
          timestamp: new Date(),
          liked: false,
          reaction: ""
        }
        this.messages.push(newMessage)
      }
    },
    formatTime(timestamp) {
      return moment(timestamp).format("HH:mm") // Display only time
    },
    formatDate(date) {
      const today = moment().format("YYYY-MM-DD")
      const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD")

      if (date === today) {
        return "Today"
      } else if (date === yesterday) {
        return "Yesterday"
      } else {
        return moment(date).format("MMM D, YYYY") // For other dates, use this format
      }
    },
    getUserPhoto(sender_id) {
      const user = this.users.find((u) => u.id === sender_id)
      return user?.avatar ? user.avatar : "/src/assets/default-avatar.jpg"
    }
  }
}
</script>

<style scoped>
.chat-messages {
  max-height: 400px;
  overflow-y: auto;
}
</style>
